import type { ServicePhase, ServiceRow } from '@busy/api';

// Custom-services CSV format — one row per phase, grouped by service name.
// Designed for barber-to-barber sharing within a shop: one barber exports
// their menu, hands the file off (AirDrop / email / Slack), the other
// barber imports it and gets the same cut types pre-populated.
//
// Columns:
//   service_name      — service title (groups rows together)
//   price_cents       — integer cents (use 4000 for $40)
//   icon              — one of scissors | kit | razor | baby | bolt
//   is_addon          — true | false (false = main service, true = add-on)
//   phase_order       — 1, 2, 3… ordering within the service
//   phase_name        — phase title (e.g. "Setup", "Fade")
//   phase_seconds     — integer seconds (use 300 for 5 minutes)
//   phase_category    — one of setup|bulk|fade|lineup|finish|beard|hottowel|shave|single
//
// Round-trip safe: export → import == identical data (modulo internal
// columns like id / created_at / archived state that aren't in the file).
// All rows for a given service_name MUST share the service-level fields
// (price_cents, icon, is_addon); we accept the first row's values as
// authoritative and ignore later disagreements with a warning.

export const SERVICE_CSV_HEADER = [
  'service_name',
  'price_cents',
  'icon',
  'is_addon',
  'phase_order',
  'phase_name',
  'phase_seconds',
  'phase_category',
] as const;

const VALID_ICONS = new Set<ServiceRow['icon']>(['scissors', 'kit', 'razor', 'baby', 'bolt']);
const VALID_PHASE_CATS = new Set<ServicePhase['cat']>([
  'setup', 'bulk', 'fade', 'lineup', 'finish', 'beard', 'hottowel', 'shave', 'single',
]);

/** Shape we hand to createService when committing an import. Mirrors
 *  ServiceRow but omits server-managed fields (id, user_id, created_at,
 *  is_archived). */
export type ImportableService = {
  name: string;
  price_cents: number;
  icon: ServiceRow['icon'];
  is_addon: boolean;
  phases: ServicePhase[];
  duration_min: number; // derived from sum of phase seconds
};

export type ParseResult = {
  /** Services that parsed clean and are safe to import. */
  services: ImportableService[];
  /** Per-row warnings — usually about a single bad row inside an
   *  otherwise-valid service. The good rows still surface. */
  warnings: string[];
  /** Hard errors that prevented any import (missing header, totally
   *  malformed file). When this is non-empty, services is []. */
  errors: string[];
};

// ────────────────────────────────────────────────────────────────────────────
// EXPORT — services → CSV string
// ────────────────────────────────────────────────────────────────────────────

/** Serialize a user's custom services to a CSV string. One row per
 *  phase, grouped by service name. Services with zero phases are
 *  emitted as a single row with empty phase fields so they round-trip
 *  (rare; phases is enforced at insert time but old data may differ). */
export function servicesToCsv(services: Array<Pick<ServiceRow, 'name' | 'price_cents' | 'icon' | 'is_addon' | 'phases'>>): string {
  const lines: string[] = [];
  lines.push(SERVICE_CSV_HEADER.join(','));
  for (const s of services) {
    if (s.phases.length === 0) {
      lines.push(
        [
          csvEscape(s.name),
          String(s.price_cents),
          s.icon,
          String(s.is_addon),
          '1',
          '',
          '0',
          'single',
        ].join(','),
      );
      continue;
    }
    s.phases.forEach((p, i) => {
      lines.push(
        [
          csvEscape(s.name),
          String(s.price_cents),
          s.icon,
          String(s.is_addon),
          String(i + 1),
          csvEscape(p.name),
          String(p.sec),
          p.cat,
        ].join(','),
      );
    });
  }
  return lines.join('\n') + '\n';
}

function csvEscape(value: string): string {
  // RFC 4180 escape: wrap in quotes if the value contains a comma, a
  // quote, or a newline; double any internal quotes.
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

// ────────────────────────────────────────────────────────────────────────────
// IMPORT — CSV string → services
// ────────────────────────────────────────────────────────────────────────────

/** Parse a CSV string into importable services. Tolerant of:
 *  - BOM at start (Excel / Numbers exports)
 *  - CRLF line endings
 *  - Quoted strings with embedded commas
 *  - Reordered or missing optional columns (icon, is_addon default
 *    sensibly if absent)
 *  - Blank lines + trailing newlines
 *
 *  Returns warnings for skipped rows and errors for catastrophic
 *  failures (missing header, unrecognized format). */
export function parseServicesCsv(rawText: string): ParseResult {
  const warnings: string[] = [];
  // Strip BOM, normalize line endings.
  const text = rawText.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
  const allLines = text.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
  if (allLines.length === 0) {
    return { services: [], warnings, errors: ['CSV file is empty.'] };
  }

  // Parse header — case-insensitive, allows arbitrary column order, only
  // requires the four "required" columns. Optional columns default if
  // missing.
  const headerCells = parseCsvLine(allLines[0]!).map((c) => c.trim().toLowerCase());
  const required = ['service_name', 'phase_name', 'phase_seconds', 'phase_category'];
  const missing = required.filter((r) => !headerCells.includes(r));
  if (missing.length > 0) {
    return {
      services: [],
      warnings,
      errors: [`CSV header missing required column(s): ${missing.join(', ')}.`],
    };
  }
  const idx = (col: string) => headerCells.indexOf(col);
  const cService = idx('service_name');
  const cPrice = idx('price_cents');
  const cIcon = idx('icon');
  const cAddon = idx('is_addon');
  const cOrder = idx('phase_order');
  const cPhaseName = idx('phase_name');
  const cPhaseSec = idx('phase_seconds');
  const cPhaseCat = idx('phase_category');

  // Group rows by service_name. First valid row's service-level fields
  // win (price / icon / is_addon).
  type ParsedRow = {
    name: string;
    price_cents: number;
    icon: ServiceRow['icon'];
    is_addon: boolean;
    phaseOrder: number;
    phase: ServicePhase;
  };
  const grouped = new Map<string, ParsedRow[]>();

  for (let i = 1; i < allLines.length; i++) {
    const lineNum = i + 1;
    const cells = parseCsvLine(allLines[i]!);
    const name = (cells[cService] ?? '').trim();
    if (!name) {
      warnings.push(`Row ${lineNum}: missing service_name — skipped.`);
      continue;
    }
    const phaseName = (cells[cPhaseName] ?? '').trim();
    const phaseSecRaw = (cells[cPhaseSec] ?? '').trim();
    const phaseSec = Number(phaseSecRaw);
    if (!Number.isFinite(phaseSec) || phaseSec < 0) {
      warnings.push(`Row ${lineNum} (${name}): invalid phase_seconds "${phaseSecRaw}" — skipped.`);
      continue;
    }
    const phaseCat = (cells[cPhaseCat] ?? '').trim().toLowerCase() as ServicePhase['cat'];
    if (!VALID_PHASE_CATS.has(phaseCat)) {
      warnings.push(
        `Row ${lineNum} (${name}): invalid phase_category "${phaseCat}" — must be one of ${[...VALID_PHASE_CATS].join('/')}. Row skipped.`,
      );
      continue;
    }
    // Price, icon, is_addon — optional, defaults applied
    const priceRaw = cPrice >= 0 ? (cells[cPrice] ?? '').trim() : '';
    const priceCents = priceRaw === '' ? 0 : Number(priceRaw);
    if (!Number.isFinite(priceCents) || priceCents < 0) {
      warnings.push(`Row ${lineNum} (${name}): invalid price_cents "${priceRaw}" — defaulted to 0.`);
    }
    const iconRaw =
      (cIcon >= 0 ? (cells[cIcon] ?? '').trim().toLowerCase() : 'scissors') as ServiceRow['icon'];
    const icon = VALID_ICONS.has(iconRaw) ? iconRaw : 'scissors';
    if (cIcon >= 0 && iconRaw && !VALID_ICONS.has(iconRaw)) {
      warnings.push(`Row ${lineNum} (${name}): invalid icon "${iconRaw}" — defaulted to scissors.`);
    }
    const addonRaw = (cAddon >= 0 ? (cells[cAddon] ?? '').trim().toLowerCase() : 'false');
    const is_addon = addonRaw === 'true' || addonRaw === '1' || addonRaw === 'yes';

    const orderRaw = cOrder >= 0 ? (cells[cOrder] ?? '').trim() : '';
    const phaseOrder = Number.isFinite(Number(orderRaw)) && orderRaw !== ''
      ? Number(orderRaw)
      : (grouped.get(name)?.length ?? 0) + 1; // fall back to insertion order

    const row: ParsedRow = {
      name,
      price_cents: Number.isFinite(priceCents) && priceCents >= 0 ? Math.round(priceCents) : 0,
      icon,
      is_addon,
      phaseOrder,
      phase: { name: phaseName || 'Phase', sec: Math.round(phaseSec), cat: phaseCat },
    };
    const bucket = grouped.get(name) ?? [];
    bucket.push(row);
    grouped.set(name, bucket);
  }

  const services: ImportableService[] = [];
  for (const [name, rows] of grouped.entries()) {
    rows.sort((a, b) => a.phaseOrder - b.phaseOrder);
    const first = rows[0]!;
    // Synthetic single-phase service: when the only row has empty
    // phase_name + 0 seconds + category='single', collapse phases to []
    // (matches CutTimerEditorPanel's "Use phases OFF" mode).
    const phases: ServicePhase[] =
      rows.length === 1 && rows[0]!.phase.sec === 0 && rows[0]!.phase.cat === 'single'
        ? []
        : rows.map((r) => r.phase);
    const duration_min = Math.max(
      1,
      Math.round(phases.reduce((s, p) => s + p.sec, 0) / 60) || 1,
    );
    services.push({
      name,
      price_cents: first.price_cents,
      icon: first.icon,
      is_addon: first.is_addon,
      phases,
      duration_min,
    });
  }

  return { services, warnings, errors: [] };
}

/** Parse a single CSV line into cells. RFC-4180-ish: handles quoted
 *  strings, escaped quotes (""), and commas inside quotes. */
function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++; // skip the escape pair's second quote
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      cells.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  cells.push(current);
  return cells;
}
